import { Box } from "@mui/material";
import React from "react";
import useTheme from "../../../../../../../common/hooks/useTheme";
import RestDayIconSvg from "../../../../../../../common/svgs/RestDayIconSvg";
import { formatWeekdayShortDate } from "../../../../../../../common/utils/date";
import { Theme } from "../../../../../../../providers/ThemeProvider";
import WorkoutIcon from "../../../../../svg/WorkoutIcon";
import ScrollableWrapper from "../../../../common/ScrollableWrapper";
import { isRestDay } from "../../helpers/activitySelectors";
import { useWorkoutPlan } from "../../store/useWorkoutPlan";
import ServiceContent from "./ServiceContent";
import TrackedActivityContent from "./TrackedActivityContent";
import WorkoutDetailsContent from "./WorkoutDetailsContent";

const REST_DAY_HINT = "Take a well-deserved break!";

/**
 * The right half of the Workout page: the selected entry's details — a
 * planned workout, a rest day, a wearable-recorded activity or a coached
 * session — or a prompt to pick one. A recorded session's analysis opens in
 * the side panel over the page (`WorkoutAnalysisPanel`), so the pane keeps
 * the entry on show. The selection is resolved against the
 * week on screen, so stepping to another week shows the prompt until an
 * entry of that week is picked (and coming back restores the selection).
 */
const DetailsPane = () => {
  const theme: Theme = useTheme();
  const { details, getActivity, getEvent, getService } = useWorkoutPlan();

  // The prompt and the rest-day view are short messages: they fill the pane
  // and centre on it, instead of sitting at its top.
  const resolve = (): { node: React.ReactNode; centred: boolean } => {
    if (!details) return { node: <EmptyDetails />, centred: true };
    if (details.kind === "workout") {
      const activity = getActivity(details.date, details.id);
      if (!activity) return { node: <EmptyDetails />, centred: true };
      if (isRestDay(activity)) {
        return { node: <RestDayDetails date={details.date} />, centred: true };
      }
      return {
        node: <WorkoutDetailsContent activity={activity} date={details.date} />,
        centred: false,
      };
    }
    if (details.kind === "event") {
      const event = getEvent(details.date, details.id);
      return event
        ? { node: <TrackedActivityContent event={event} date={details.date} />, centred: false }
        : { node: <EmptyDetails />, centred: true };
    }
    const service = getService(details.id);
    return service
      ? { node: <ServiceContent service={service} date={details.date} />, centred: false }
      : { node: <EmptyDetails />, centred: true };
  };
  const { node, centred } = resolve();

  return (
    <Box sx={styles.pane(theme)}>
      <ScrollableWrapper customPadding="8px">
        <Box sx={styles.content(centred)}>
          {node}
          {!centred && <Box sx={{ height: "40px" }} />}
        </Box>
      </ScrollableWrapper>
    </Box>
  );
};

export default DetailsPane;

/** Nothing picked yet. */
const EmptyDetails = () => {
  const theme: Theme = useTheme();
  return (
    <Box sx={styles.centered}>
      <Box sx={styles.bigDisc(theme)}>
        <WorkoutIcon fill={theme.colors_ds.NEUTRAL_ICON_WEAK} />
      </Box>
      <Box sx={styles.emptyTitle(theme)}>No workout selected</Box>
      <Box sx={styles.emptyText(theme)}>
        Pick a workout, tracked activity or session from the week to see its details here.
      </Box>
    </Box>
  );
};

/** A rest day: the mobile card's glyph and hint, with the day. */
const RestDayDetails = ({ date }: { date: string }) => {
  const theme: Theme = useTheme();
  return (
    <Box sx={styles.centered}>
      <Box sx={styles.bigDisc(theme)}>
        <RestDayIconSvg fill={theme.colors_ds.NEUTRAL_TEXT_STRONG} width={28} height={28} />
      </Box>
      <Box sx={styles.restDate(theme)}>{formatWeekdayShortDate(date)}</Box>
      <Box sx={styles.restTitle(theme)}>Rest Day</Box>
      <Box sx={styles.emptyText(theme)}>{REST_DAY_HINT}</Box>
    </Box>
  );
};

const styles = {
  pane: (theme: Theme) => ({
    minHeight: 0,
    display: "flex",
    flexDirection: "column" as "column",
    padding: "16px 0 0 16px",
    borderRadius: "16px",
    border: `1px solid ${theme.colors_ds.NEUTRAL_BORDER_WEAK}`,
    backgroundColor: theme.colors_ds.NEUTRAL_BG_MAIN,
  }),
  content: (centred: boolean) => ({
    display: "flex",
    flexDirection: "column" as "column",
    gap: "24px",
    // The ScrollableWrapper's 8px padding + 8px gutter make the 16px right edge.
    paddingRight: 0,
    // A centred message takes the whole scroll area so it sits mid-pane.
    ...(centred ? { flex: 1, minHeight: "100%" } : {}),
  }),
  centered: {
    flex: 1,
    display: "flex",
    flexDirection: "column" as "column",
    alignItems: "center",
    justifyContent: "center",
    textAlign: "center" as "center",
    gap: "8px",
    padding: "32px 24px",
  },
  bigDisc: (theme: Theme) => ({
    display: "flex",
    alignItems: "center",
    justifyContent: "center",
    width: "64px",
    height: "64px",
    marginBottom: "8px",
    borderRadius: "32px",
    backgroundColor: theme.colors_ds.NEUTRAL_BG_ELEVATION1,
  }),
  emptyTitle: (theme: Theme) => ({
    color: theme.colors_ds.NEUTRAL_TEXT_STRONG,
    fontFamily: "var(--font-Medium)",
    fontSize: "16px",
    lineHeight: "24px",
  }),
  emptyText: (theme: Theme) => ({
    maxWidth: "320px",
    color: theme.colors_ds.NEUTRAL_TEXT_WEAK,
    fontFamily: "var(--font-Regular)",
    fontSize: "14px",
    lineHeight: "20px",
  }),
  restDate: (theme: Theme) => ({
    color: theme.colors_ds.NEUTRAL_TEXT_WEAK,
    fontFamily: "var(--font-Regular)",
    fontSize: "14px",
    lineHeight: "20px",
  }),
  restTitle: (theme: Theme) => ({
    color: theme.colors_ds.NEUTRAL_TEXT_STRONG,
    fontFamily: "var(--font-Bold)",
    fontSize: "20px",
    lineHeight: "28px",
  }),
};
